import { API } from '~apis/axios/instance';
import { CustomLabel } from '~models/custom-labels';
import { ResponseBaseWithData } from '~types/response';

export interface CompanyIdentifier {
  companyId: string;
}

export const getCustomLabels = ({ companyId }: CompanyIdentifier) => {
  return API.get<ResponseBaseWithData<CustomLabel.List>>(
    `/api/v2/companies/${companyId}/custom-labels`,
  );
};

interface GroupTreeResponse {
  tree: ITree;
}

interface ITree {
  children: GroupTree[];
}

export type GroupTree = {
  key: string;
  value: GroupTreeItem;
  children: GroupTree[];
};

export type GroupTreeItem = {
  id: number;
  companyId: string;
  name: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  path: string;
  isActive: boolean;
};

export const getGroupLabelTree = ({ companyId }: CompanyIdentifier) => {
  return API.get<ResponseBaseWithData<GroupTreeResponse>>(
    `/api/v2/companies/${companyId}/custom-labels/tree`,
  );
};

interface GroupSpecificCategoryCreateRequest {
  companyId: string;
  request: {
    name: string;
    description?: string;
    parentId?: number;
  };
}

export const addGroupSpecificLabel = ({
  companyId,
  request,
}: GroupSpecificCategoryCreateRequest) => {
  return API.post<
    ResponseBaseWithData<{
      categoryTreeKey: string;
    }>
  >(`/api/v2/companies/${companyId}/custom-labels`, {
    data: request,
  });
};

export const moveGroupParentCategory = ({
  companyId,
  id,
  newParentKey,
}: {
  companyId: string;
  id: number;
  newParentKey: number;
}) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/custom-labels/${id}/move`,
    {
      data: {
        newParentKey,
      },
    },
  );
};

export interface DeleteRequestParams {
  moveChildrenTo?: string | null;
  migrations?: {
    from: number;
    to: number | null;
  }[];
}

export const deleteGroupSpecificLabel = ({
  companyId,
  id,
  request,
}: {
  companyId: string;
  id: number;
  request: DeleteRequestParams;
}) => {
  return API.delete<ResponseBaseWithData>(`/api/v2/companies/${companyId}/custom-labels/${id}`, {
    data: request,
  });
};

interface CustomLabelRequest {
  name: string;
  description: string;
  companyId: string;
  id: number;
}

export const createCustomLabel = ({
  companyId,
  name,
  description,
}: Omit<CustomLabelRequest, 'id'>) => {
  return API.post<ResponseBaseWithData>(`/api/v2/companies/${companyId}/custom-labels`, {
    data: {
      name,
      description,
    },
  });
};

export const updateCustomLabel = ({ companyId, id, name, description }: CustomLabelRequest) => {
  return API.put<ResponseBaseWithData>(`/api/v2/companies/${companyId}/custom-labels/${id}`, {
    data: {
      name,
      description,
    },
  });
};

export const toggleCustomLabel = ({
  companyId,
  id,
  active,
}: {
  companyId: string;
  id: number;
  active: boolean;
}) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/custom-labels/${id}/active`,
    {
      data: {
        active,
      },
    },
  );
};

export const reorderCustomLabel = ({
  companyId,
  request,
}: {
  companyId: string;
  request: {
    parentId: number | null;
    ids: number[];
  };
}) => {
  return API.patch<ResponseBaseWithData>(`/api/v2/companies/${companyId}/custom-labels/reorder`, {
    data: request,
  });
};

export const deleteCustomLabel = ({ companyId, id }: { companyId: string; id: number }) => {
  return API.delete<ResponseBaseWithData>(`/api/v2/companies/${companyId}/custom-labels/${id}`);
};
